import React, { useState, useEffect, useRef, useCallback } from "react"
import { controllers } from "../../Controllers/Controllers"
import { navigator } from "../../Controllers/Navigator"
import './notification-bell-styles.css'

type ReplyNotification = {
    id: number
    lead_id: number | null
    lead_company_name: string | null
    subject: string
    from_address: string
    preview: string
    is_read: boolean
    created_at: string
}

const POLL_INTERVAL_MS = 30000

function formatRelativeTime(isoString: string): string {
    const then = new Date(isoString).getTime()
    const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000))
    if (diffSec < 45) return 'just now'
    const diffMin = Math.floor(diffSec / 60)
    if (diffMin < 60) return `${diffMin}m ago`
    const diffHr = Math.floor(diffMin / 60)
    if (diffHr < 24) return `${diffHr}h ago`
    const diffDay = Math.floor(diffHr / 24)
    if (diffDay < 7) return `${diffDay}d ago`
    return new Date(isoString).toLocaleDateString()
}

function NotificationBell() {
    const [open, setOpen] = useState(false)
    const [notifications, setNotifications] = useState<ReplyNotification[]>([])
    const [unreadCount, setUnreadCount] = useState(0)
    const [loading, setLoading] = useState(false)
    const [justBumped, setJustBumped] = useState(false)
    const containerRef = useRef<HTMLDivElement>(null)
    const pollRef = useRef<number | null>(null)
    const prevUnreadRef = useRef(0)
    const firstFetchRef = useRef(true)

    const fetchNotifications = useCallback(async () => {
        if (!controllers().auth.userToken) return
        const response = await controllers().report.getReplyNotifications(20)
        if (!response || response.statusCode !== 200) return
        const data = response.data || {}
        const list: ReplyNotification[] = data.notifications || []
        const count: number = data.unread_count ?? 0

        setNotifications(list)
        setUnreadCount(count)

        // Only bump-animate on counts that arrive *after* the first load,
        // so opening the app with 4 unread doesn't itself trigger the
        // "new stuff!" animation.
        if (!firstFetchRef.current && count > prevUnreadRef.current) {
            setJustBumped(true)
            setTimeout(() => setJustBumped(false), 1200)
        }
        firstFetchRef.current = false
        prevUnreadRef.current = count
    }, [])

    // Poll on an interval, paused while the tab is hidden so a backgrounded
    // tab doesn't keep hammering the API, and refreshed immediately on
    // regaining focus so the badge is never stale for longer than the user
    // was actually away.
    useEffect(() => {
        fetchNotifications()

        const startPolling = () => {
            if (pollRef.current) return
            pollRef.current = window.setInterval(fetchNotifications, POLL_INTERVAL_MS)
        }
        const stopPolling = () => {
            if (pollRef.current) {
                window.clearInterval(pollRef.current)
                pollRef.current = null
            }
        }
        const handleVisibility = () => {
            if (document.hidden) {
                stopPolling()
            } else {
                fetchNotifications()
                startPolling()
            }
        }

        startPolling()
        document.addEventListener('visibilitychange', handleVisibility)
        return () => {
            stopPolling()
            document.removeEventListener('visibilitychange', handleVisibility)
        }
    }, [fetchNotifications])

    useEffect(() => {
        if (!open) return
        const handleClick = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClick)
        return () => document.removeEventListener('mousedown', handleClick)
    }, [open])

    // const handleMarkAllRead = async (e: React.MouseEvent) => {
    //     e.stopPropagation()
    //     if (loading || unreadCount === 0) return
    //     setLoading(true)
    //     await controllers().report.markAllLogsRead()
    //     await fetchNotifications()
    //     setLoading(false)
    // }

    // const handleSelectNotification = (log: ReplyNotification) => {
    //     if (!log.is_read) {
    //         controllers().report.markLogRead(log.id)
    //         setNotifications(prev => prev.map(n => n.id === log.id ? { ...n, is_read: true } : n))
    //         setUnreadCount(prev => Math.max(0, prev - 1))
    //         prevUnreadRef.current = Math.max(0, prevUnreadRef.current - 1)
    //     }
    //     setOpen(false)
    //     // Adjust this to wherever a single lead's thread actually lives —
    //     // this assumes the Report page can filter by lead_id.
    //     navigator().navigate(log.lead_id ? `/outreach/communications/${log.lead_id}` : '/outreach/communications')
    // }

    const handleMarkAllRead = async (e: React.MouseEvent) => {
        e.stopPropagation()
        if (loading || unreadCount === 0) return
        setLoading(true)
        await controllers().report.markAllReplyNotificationsRead()
        await fetchNotifications()
        setLoading(false)
    }

    const handleSelectNotification = (log: ReplyNotification) => {
        if (!log.is_read) {
            controllers().report.markReplyNotificationRead(log.id)
            setNotifications(prev => prev.map(n => n.id === log.id ? { ...n, is_read: true } : n))
            setUnreadCount(prev => Math.max(0, prev - 1))
            prevUnreadRef.current = Math.max(0, prevUnreadRef.current - 1)
        }
        setOpen(false)
        navigator().navigate(log.lead_id ? `/outreach/communications/${log.lead_id}` : '/outreach/communications')
    }

    return (
        <div className="nb-container" ref={containerRef}>
            <button
                className={`nb-bell-btn ${justBumped ? 'nb-bump' : ''}`}
                onClick={() => setOpen(prev => !prev)}
                aria-label="Reply notifications"
            >
                <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
                {unreadCount > 0 && (
                    <span className="nb-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
                )}
            </button>

            {open && (
                <div className="nb-panel">
                    <div className="nb-panel-header">
                        <span>Replies</span>
                        {unreadCount > 0 && (
                            <button className="nb-mark-all" onClick={handleMarkAllRead} disabled={loading}>
                                Mark all read
                            </button>
                        )}
                    </div>

                    <div className="nb-list">
                        {notifications.length === 0 && (
                            <div className="nb-empty">No replies yet</div>
                        )}
                        {notifications.map(log => (
                            <button
                                key={log.id}
                                className={`nb-item ${!log.is_read ? 'nb-item-unread' : ''}`}
                                onClick={() => handleSelectNotification(log)}
                            >
                                <span className="nb-item-dot" />
                                <span className="nb-item-body">
                                    <span className="nb-item-title">
                                        {log.lead_company_name || log.from_address} replied
                                    </span>
                                    {log.preview && <span className="nb-item-note">{log.preview}</span>}
                                    <span className="nb-item-time">{formatRelativeTime(log.created_at)}</span>
                                </span>
                            </button>
                        ))}
                    </div>

                    <div className="nb-panel-footer">
                        <button
                            className="nb-view-all"
                            onClick={() => { setOpen(false); navigator().navigate('/outreach/report') }}
                        >
                            View all reports
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}

export { NotificationBell }